import { requireSupabase } from "./supabase";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

function buffersMatch(left: ArrayBuffer | null, right: Uint8Array): boolean {
  if (!left) return false;
  const leftBytes = new Uint8Array(left);
  if (leftBytes.length !== right.length) return false;
  return leftBytes.every((byte, index) => byte === right[index]);
}

function arrayBufferToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export function canUseWebPush(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window &&
    Boolean(import.meta.env.VITE_VAPID_PUBLIC_KEY)
  );
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker.register("/sw.js");
}

async function registerSubscription(subscription: PushSubscription): Promise<string> {
  const key = subscription.getKey("p256dh");
  const auth = subscription.getKey("auth");

  const { data, error } = await requireSupabase().rpc("register_web_push_subscription", {
    subscription_endpoint: subscription.endpoint,
    subscription_p256dh: arrayBufferToBase64Url(key),
    subscription_auth: arrayBufferToBase64Url(auth),
    subscription_user_agent: navigator.userAgent
  });

  if (error) throw error;
  return data as string;
}

async function getOrCreateSubscription(): Promise<PushSubscription> {
  const registration = await registerServiceWorker();
  if (!registration) {
    throw new Error("Service worker er ikke tilgjengelig.");
  }

  const applicationServerKey = urlBase64ToUint8Array(import.meta.env.VITE_VAPID_PUBLIC_KEY as string);
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    if (buffersMatch(existing.options.applicationServerKey, applicationServerKey)) {
      return existing;
    }
    await existing.unsubscribe();
  }

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey
  });
}

export async function syncGrantedWebPush(): Promise<string | null> {
  if (!canUseWebPush() || Notification.permission !== "granted") return null;
  return registerSubscription(await getOrCreateSubscription());
}

export async function requestAndRegisterWebPush(): Promise<string> {
  if (!canUseWebPush()) {
    throw new Error("Web push is not configured for this build.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Varsler ble ikke slått på.");
  }

  return registerSubscription(await getOrCreateSubscription());
}
